import React from 'react';
import { useNavigate } from 'react-router-dom';

const SystemFlowIndex = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: 'System Architecture Overview',
      description: 'High-level view of the Way2Connect SMPP Platform — all services, Kafka pipeline, Redis caching, workers, and the overall data flow.',
      icon: '🏗️',
      href: '/system-flow/architecture',
      color: '#3B82F6',
      stats: ['5 Services', 'Kafka Pipeline', 'Redis + Nginx']
    },
    {
      title: 'SMS Delivery Flow',
      description: 'End-to-end SMS journey: from user submission (GUI/HTTP API/JSON API/SMPP) through Kafka ingestion, balance gate, dedup, routing, submission, and DLR callback.',
      icon: '📨',
      href: '/system-flow/sms-flow',
      color: '#10B981',
      stats: ['4 Channels', 'Kafka + DB Dual', 'Balance Gate', 'DLR Callback']
    },
    {
      title: 'Microservices Detail',
      description: 'Deep dive into each service — Backend API, HTTP Submit Worker, SMPP Worker, DLR Processor, and background workers.',
      icon: '⚙️',
      href: '/system-flow/microservices',
      color: '#8B5CF6',
      stats: ['Backend Server', 'HTTP Worker', 'SMPP Worker', 'DLR Processor']
    },
    {
      title: 'Database Schema',
      description: 'Complete database documentation — all databases, every table, every column with types and keys. Searchable and filterable.',
      icon: '🗄️',
      href: '/system-flow/databases',
      color: '#F59E0B',
      stats: ['8 Databases', '130+ Tables', 'Partitioned Reports']
    },
    {
      title: 'Authentication & Security',
      description: 'Login flow, JWT token lifecycle, Redis session management, rate limiting, password policies, and security headers.',
      icon: '🔒',
      href: '/system-flow/authentication',
      color: '#EF4444',
      stats: ['bcrypt + JWT', 'Redis Sessions', 'Rate Limiting']
    },
    {
      title: 'API Reference',
      description: 'All REST API endpoints organized by module — authentication, SMS (GUI/HTTP/JSON), admin, SMPP management, reports, and more.',
      icon: '🔌',
      href: '/system-flow/api-reference',
      color: '#06B6D4',
      stats: ['60+ Endpoints', 'JWT Auth', 'API Key Auth']
    },
  ];

  const quickLinks = [
    { label: 'Knowledge Base', href: '/knowledge-base', icon: '📚' },
    { label: 'Live Support', href: '/support/dashboard', icon: '💬' },
    { label: 'System Status', href: '/system/status', icon: '🟢' },
    { label: 'Platform Changelog', href: '/changelog', icon: '📋' },
    { label: 'Keyboard Shortcuts', href: '/shortcuts', icon: '⌨️' },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .shadow-lg, .shadow-md, .shadow-sm { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
        }
      `}</style>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">System Flow Documentation</h1>
            <p className="mt-2 text-gray-600">
              Complete technical documentation of the Way2Connect SMPP Platform. 
              Understand how every component works, connects, and processes data.
            </p>
          </div>
          <button
            onClick={() => window.print()}
            className="no-print flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
          >
            <span>🖨️</span> Print
          </button>
        </div>

        {/* Platform Summary Bar */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Platform', value: 'Way2Connect SMPP', color: '#3B82F6' },
            { label: 'Language', value: 'Go 1.21 + React 18', color: '#10B981' },
            { label: 'Database', value: 'MariaDB 10.11', color: '#F59E0B' },
            { label: 'Queue', value: 'Kafka + Redis', color: '#EF4444' },
            { label: 'Proxy', value: 'Nginx + Cloudflare', color: '#8B5CF6' },
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-3 text-center">
              <div className="text-xs text-gray-500 uppercase tracking-wider">{item.label}</div>
              <div className="mt-1 text-sm font-semibold" style={{ color: item.color }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Documentation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {sections.map((section, index) => (
          <div
            key={index}
            onClick={() => navigate(section.href)}
            className="no-print-break bg-white rounded-xl border border-gray-200 p-6 cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all duration-200 group"
          >
            <div className="flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                style={{ backgroundColor: `${section.color}15` }}
              >
                {section.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                  {section.title}
                </h3>
                <p className="mt-1 text-sm text-gray-500 line-clamp-2">{section.description}</p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {section.stats.map((stat, i) => (
                <span
                  key={i}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: `${section.color}10`, color: section.color }}
                >
                  {stat}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Links</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {quickLinks.map((link, i) => (
            <button
              key={i}
              onClick={() => navigate(link.href)}
              className="no-print flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-colors text-sm font-medium text-gray-700"
            >
              <span>{link.icon}</span>
              {link.label}
            </button>
          ))}
        </div>
      </div>

      {/* Current SMS Pipeline Flow */}
      <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Current SMS Pipeline Flow</h2>
        <p className="text-sm text-gray-500 mb-4">Production pipeline as of Feb 2026 — Kafka primary, DB fallback</p>
        <div className="overflow-x-auto">
          <div className="flex items-center gap-2 flex-wrap text-xs font-mono py-3 px-2 rounded-lg" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            {[
              { label: 'INGEST', desc: 'GUI / HTTP API / JSON API / SMPP', color: '#3B82F6' },
              { label: 'KAFKA', desc: 'sms.requests topic (DB fallback)', color: '#F59E0B' },
              { label: 'DEDUP', desc: 'Redis SHA256 (10min TTL)', color: '#8B5CF6' },
              { label: 'BALANCE', desc: 'Redis Lua atomic reserve', color: '#EF4444' },
              { label: 'REPORT', desc: 'Batch INSERT to reporting DB', color: '#06B6D4' },
              { label: 'ROUTE', desc: 'Redis cached route resolution', color: '#10B981' },
              { label: 'SUBMIT', desc: 'HTTP or SMPP to operator', color: '#F97316' },
              { label: 'DLR', desc: 'Callback to user DLR URL', color: '#EC4899' },
            ].map((step, i) => (
              <React.Fragment key={step.label}>
                {i > 0 && <span className="text-gray-300 font-bold">→</span>}
                <div className="inline-flex flex-col items-center px-3 py-2 rounded-lg" style={{ background: `${step.color}08`, border: `1px solid ${step.color}20` }}>
                  <span className="font-bold" style={{ color: step.color }}>{step.label}</span>
                  <span className="text-[10px] text-gray-500 mt-0.5 text-center whitespace-nowrap">{step.desc}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Architecture Quick View (for print) */}
      <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Platform Architecture Summary</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 font-semibold text-gray-700">Component</th>
                <th className="text-left py-2 px-3 font-semibold text-gray-700">Technology</th>
                <th className="text-left py-2 px-3 font-semibold text-gray-700">Port</th>
                <th className="text-left py-2 px-3 font-semibold text-gray-700">Service</th>
                <th className="text-left py-2 px-3 font-semibold text-gray-700">Purpose</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[
                ['Backend API Server', 'Go (Gin)', '8092', 'gosmpp-backend', 'REST API, Auth, Admin, SMPP Mgmt, Inbound SMPP (port 2776)'],
                ['HTTP Submit Worker', 'Go', 'N/A', 'http-submit-worker', 'Kafka consumer — process HTTP delivery SMS, balance gate, dedup'],
                ['SMPP Worker', 'Go', 'N/A', 'smpp-worker', 'Kafka consumer — SMPP delivery, outbound connections, DLR processing'],
                ['Kafka Broker', 'Apache Kafka', '9092', 'kafka (Docker)', 'Message queue — sms.requests, sms.retries, sms.dlrs, sms.deadletter'],
                ['Zookeeper', 'Apache Zookeeper', '2181', 'zookeeper (Docker)', 'Kafka cluster coordination'],
                ['Frontend', 'React 18 + Tailwind', '80/443', 'nginx', 'Admin Panel, User Dashboard, SMS GUI, System Flow Docs'],
                ['Reverse Proxy', 'Nginx', '80/443', 'nginx', 'SSL termination, routing, rate limiting, static files'],
                ['Redis', 'Redis 7', '6379', 'redis', 'Sessions, balance, TPS, routing cache, dedup, validation cache'],
                ['Primary Database', 'MariaDB 10.11', '3306', 'mariadbd', 'Users, DLT, Plans, Routing, Config, Balances'],
                ['Reporting Database', 'MariaDB 10.11', '3306', 'mariadbd', 'sms_reports_partitioned (daily partitions, 90-day retention)'],
              ].map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  {row.map((cell, j) => (
                    <td key={j} className={`py-2 px-3 ${j === 0 ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Background Workers */}
      <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Background Workers & Cron Jobs</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 font-semibold text-gray-700">Worker</th>
                <th className="text-left py-2 px-3 font-semibold text-gray-700">Runs In</th>
                <th className="text-left py-2 px-3 font-semibold text-gray-700">Interval</th>
                <th className="text-left py-2 px-3 font-semibold text-gray-700">Purpose</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[
                ['Reporting Aggregator', 'gosmpp-backend', 'Every 5 min', 'Aggregate SMS stats into daily/monthly summaries'],
                ['DLR Callback Worker', 'gosmpp-backend', 'Every 10 sec', 'Send delivery reports to user DLR URLs'],
                ['Log Cleanup Worker', 'gosmpp-backend', 'Every 6 hours', 'Delete old worker_metrics, worker_logs, auth_audit_log'],
                ['SMS Dedup Cleanup', 'Redis', 'Auto (TTL)', 'SHA256 dedup keys auto-expire after 10 minutes'],
                ['Partition Maintenance', 'Cron (daily)', 'Midnight', 'Create tomorrow\'s partition, drop partitions > 90 days'],
                ['Balance Gate', 'http-submit-worker', 'Per batch', 'Redis Lua atomic balance reserve before SMS processing'],
                ['Route Cache', 'Workers', '5 min TTL', 'Redis-cached route resolution for SMS routing'],
                ['Validation Cache', 'Backend', '5 min TTL', 'Redis-cached entity/header/template validation'],
              ].map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  {row.map((cell, j) => (
                    <td key={j} className={`py-2 px-3 ${j === 0 ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Deprecated Components */}
      <div className="mt-6 bg-white rounded-xl border border-amber-200 p-6" style={{ background: 'rgba(245, 158, 11, 0.02)' }}>
        <h2 className="text-lg font-semibold text-amber-800 mb-2">⚠️ Deprecated Components</h2>
        <p className="text-sm text-amber-600 mb-4">These components were part of the original architecture but have been replaced or are no longer in active use.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-amber-200">
                <th className="text-left py-2 px-3 font-semibold text-amber-700">Component</th>
                <th className="text-left py-2 px-3 font-semibold text-amber-700">Status</th>
                <th className="text-left py-2 px-3 font-semibold text-amber-700">Replaced By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-100">
              {[
                ['API Gateway (microservices/api-gateway)', 'Deprecated', 'All routing handled by Nginx + Backend directly'],
                ['Auth Service (microservices/auth-service)', 'Deprecated', 'Auth built into Backend API Server'],
                ['Billing Service (microservices/billing-service)', 'Deprecated', 'Redis Lua atomic balance in Backend + Workers'],
                ['DB-only SMS queue (sms_ingest_requests polling)', 'Fallback only', 'Kafka sms.requests is primary; DB is fallback'],
                ['Separate DLR Processor service', 'Merged', 'DLR processing merged into SMPP Worker (smpp-worker.service)'],
              ].map((row, i) => (
                <tr key={i}>
                  <td className="py-2 px-3 font-medium text-amber-900">{row[0]}</td>
                  <td className="py-2 px-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{row[1]}</span></td>
                  <td className="py-2 px-3 text-gray-600">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SystemFlowIndex;
